import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, UploadSimple, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav, PeriodBar, usePeriods, CCStatusBadge, fmtAmt } from "../components/cc/common";
import { CCGuide } from "../components/cc/GuideBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function UploadCard({ periodKey, carriers, onUploaded, onCarrierAdded }) {
  const [docType, setDocType] = useState("carrier_statement");
  const [carrierId, setCarrierId] = useState("");
  const [statementId, setStatementId] = useState("");
  const [busy, setBusy] = useState(false);
  const [carrierDialog, setCarrierDialog] = useState(false);
  const [newCarrier, setNewCarrier] = useState({ name: "", carrier_code: "" });
  const fileRef = useRef(null);

  const addCarrier = async () => {
    try {
      const { data } = await api.post("/commission/carriers", newCarrier);
      toast.success(`Carrier ${data.name} added`);
      setCarrierDialog(false);
      setNewCarrier({ name: "", carrier_code: "" });
      onCarrierAdded(data);
      setCarrierId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add carrier");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("period_key", periodKey);
    if (docType === "carrier_statement") fd.append("carrier_id", carrierId);
    fd.append("statement_id", statementId);
    try {
      await api.post("/commission/imports/upload", fd);
      toast.success("Uploaded — extraction started. Original preserved with SHA-256 checksum.");
      setStatementId("");
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6" data-testid="cc-upload-card">
      <h2 className="font-medium text-sm mb-3 flex items-center gap-2"><UploadSimple size={17} /> Upload into {periodKey}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs text-slate-500">Document type</label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="cc-doctype-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="carrier_statement">Carrier statement</SelectItem>
              <SelectItem value="ezlynx_snapshot">EZLynx book of business</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {docType === "carrier_statement" && (
          <div>
            <label className="text-xs text-slate-500">Carrier</label>
            <div className="flex gap-1.5">
              <Select value={carrierId} onValueChange={setCarrierId}>
                <SelectTrigger data-testid="cc-carrier-select"><SelectValue placeholder="Pick carrier" /></SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => setCarrierDialog(true)} title="Add carrier" data-testid="cc-add-carrier-btn"><Plus size={15} /></Button>
            </div>
          </div>
        )}
        <div>
          <label className="text-xs text-slate-500">Statement ID (optional)</label>
          <Input value={statementId} onChange={(e) => setStatementId(e.target.value)} placeholder="e.g. STMT-2026-05-114" data-testid="cc-statement-id-input" />
        </div>
        <div className="flex items-end">
          <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx" className="hidden" onChange={onFile} data-testid="cc-file-input" />
          <Button className="w-full" disabled={busy || (docType === "carrier_statement" && !carrierId)}
            onClick={() => fileRef.current?.click()} data-testid="cc-upload-btn">
            <FileArrowUp size={16} className="mr-1.5" /> {busy ? "Uploading…" : "Choose file (PDF / CSV / XLSX)"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">Originals are never altered. Duplicate files (same checksum) are rejected. PDFs are AI-extracted page by page — every value needs human verification before validation.</p>
      <Dialog open={carrierDialog} onOpenChange={setCarrierDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add carrier</DialogTitle></DialogHeader>
          <Input placeholder="Carrier name (e.g. Progressive)" value={newCarrier.name}
            onChange={(e) => setNewCarrier({ ...newCarrier, name: e.target.value })} data-testid="cc-carrier-name-input" />
          <Input placeholder="Code (e.g. PGR)" value={newCarrier.carrier_code}
            onChange={(e) => setNewCarrier({ ...newCarrier, carrier_code: e.target.value.toUpperCase() })} data-testid="cc-carrier-code-input" />
          <Button onClick={addCarrier} disabled={!newCarrier.name || !newCarrier.carrier_code} data-testid="cc-carrier-create-btn">Add carrier</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CommissionImportsPage() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const { periods, selected, setSelected, reload } = usePeriods();
  const [batches, setBatches] = useState([]);
  const [carriers, setCarriers] = useState([]);

  const load = async (pk = selected) => {
    if (!pk) return;
    const { data } = await api.get(`/commission/imports?period_key=${pk}`);
    setBatches(data);
  };

  useEffect(() => { api.get("/commission/carriers").then((r) => setCarriers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [selected]);

  useEffect(() => {
    if (!batches.some((b) => b.status === "extracting")) return;
    const t = setInterval(() => load(), 4000);
    return () => clearInterval(t);
  }, [batches, selected]);

  const period = periods.find((p) => p.period_key === selected);

  return (
    <div data-testid="commission-imports-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Commission imports</h1>
          <p className="text-sm text-slate-500 mt-1">Carrier statements & EZLynx snapshots — originals preserved, rows staged for review.</p>
        </div>
        <PeriodBar periods={periods} selected={selected} setSelected={setSelected}
          onCreated={async (k) => { await reload(); setSelected(k); }} />
      </div>
      <CCNav />
      <CCGuide periodKey={selected} />

      {!selected ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">
          Open a monthly period first (button above).
        </div>
      ) : (
        <>
          {hasPerm("commission.import") && period?.status === "open" && (
            <UploadCard periodKey={selected} carriers={carriers} onUploaded={load}
              onCarrierAdded={(c) => setCarriers((p) => [...p, c].sort((a, b) => a.name.localeCompare(b.name)))} />
          )}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100"><h2 className="font-medium text-sm">Imports — {selected}</h2></div>
            {batches.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">Nothing uploaded for this period yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cc-batches-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                      <th className="px-4 py-2">File</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Carrier</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2 text-right">Rows</th>
                      <th className="px-4 py-2 text-right">Extracted</th>
                      <th className="px-4 py-2 text-right">Stmt total</th>
                      <th className="px-4 py-2 text-right">Diff</th>
                      <th className="px-4 py-2">Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/commissions/imports/${b.id}`)} data-testid={`cc-batch-row-${b.id}`}>
                        <td className="px-4 py-2.5 font-medium max-w-[220px] truncate">{b.filename}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500">{b.doc_type === "carrier_statement" ? "Statement" : "EZLynx"}</td>
                        <td className="px-4 py-2.5">{b.carrier_name}</td>
                        <td className="px-4 py-2.5"><CCStatusBadge status={b.status} /></td>
                        <td className="px-4 py-2.5 text-right">{b.transaction_count}</td>
                        <td className="px-4 py-2.5 text-right">{b.doc_type === "carrier_statement" ? fmtAmt(b.extracted_total) : "—"}</td>
                        <td className="px-4 py-2.5 text-right">{b.doc_type === "carrier_statement" ? (b.source_total ? fmtAmt(b.source_total) : <span className="text-amber-600 text-xs">missing</span>) : "—"}</td>
                        <td className="px-4 py-2.5 text-right">
                          {b.discrepancy == null ? <span className="text-slate-400">—</span> :
                            Number(b.discrepancy) === 0 ? <span className="text-emerald-700">$0.00</span> :
                              <span className="text-red-600 font-medium">{fmtAmt(b.discrepancy)}</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-500">{b.uploaded_by_name}<br />{fmtDate(b.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
